import {
  currencyInfo, esPaisConocido, fechaLegible, formatCifra, monedaDe, purchaseFormatLabel, unidadVisible, type Ingredient } from '@alexiarest/shared';

export { unidadVisible };

/**
 * Formateo unico para toda la app. Regla de oro: ninguna pantalla puede
 * mostrar NaN, Infinity ni undefined, asi que todo pasa por `safe()`.
 *
 * Multi-mercado: los importes se formatean en la moneda del tenant.
 * `configureMoney()` se llama desde el Layout cuando llega el tenant y
 * reconstruye los formateadores; los numeros y fechas siguen en es-ES,
 * que es el idioma del producto.
 *
 * Formato de numeros segun el pais (Alex, 12 oct 2026), en TODA la app:
 *  - Espanya: «0,5 kg · 1.000 g · 0,28 €/kg».
 *  - Mexico: «0.5 kg · 1,000 g · $0.28/kg».
 *  - Dinero: maximo 2 decimales (tambien el costo por kg o por gramo).
 *  - Lo que es mayor que cero pero no llega al minimo visible no sale como
 *    cero: «menos de 0,01 €», «menos de 0,1 %».
 *  - El litro, siempre «L».
 * Ninguna pantalla deberia usar toFixed/toLocaleString para ensenyar una
 * cifra: todo pasa por aqui.
 */

/**
 * Locale de las CIFRAS (separador decimal y de miles). Lo fija el pais del
 * restaurante (mejora 45 de Alex, 5 sep 2026): una cuenta de Estados Unidos
 * ve «1,234.56» en todas partes, incluido el escaner; una espanyola,
 * «1.234,56». Va por pais y no por moneda, como pidio Alex. El idioma de la
 * interfaz sigue siendo espanyol.
 */
let LOCALE = 'es-ES';
let SEPARADOR_DECIMAL = ',';

/**
 * Fija el locale de las cifras a partir del pais: el de la moneda de ese
 * pais (Estados Unidos → es-US, con punto decimal). Si el pais no se conoce
 * (tenant antiguo con «OTHER»), manda la moneda del restaurante. Devuelve
 * true si cambia.
 */
export function configureLocale(country: string | undefined, currency: string | undefined): boolean {
  const moneda = esPaisConocido(country) ? monedaDe(country) : (currency ?? 'EUR');
  const next = currencyInfo(moneda).locale;
  if (next === LOCALE) return false;
  LOCALE = next;
  SEPARADOR_DECIMAL =
    new Intl.NumberFormat(LOCALE).formatToParts(1.5).find((p) => p.type === 'decimal')?.value ?? ',';
  return true;
}

export function activeLocale(): string {
  return LOCALE;
}

/** Separador decimal del locale activo: «,» en Espanya, «.» en Estados Unidos. */
export function decimalSeparator(): string {
  return SEPARADOR_DECIMAL;
}

/**
 * Un numero para un campo de texto editable, con el separador decimal del
 * locale y sin separador de miles (un campo con «1.234,5» no se parsea bien).
 */
export function formatDecimalInput(value: number, maxDecimals = 3): string {
  const rounded = Number(value.toFixed(maxDecimals));
  return String(rounded).replace('.', decimalSeparator());
}

/**
 * Un importe para un campo editable: siempre con los decimales de la moneda
 * («9,50», nunca «9,5» ni «9.5»; revisión de Alex del 14 oct 2026) y sin
 * separador de miles.
 */
export function formatMoneyInput(value: number): string {
  const n = Number.isFinite(value) ? value : 0;
  return n.toFixed(money.decimals).replace('.', decimalSeparator());
}

/** Convierte cualquier cosa en un numero finito utilizable. */
export function safe(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  // Normaliza el -0 para que nunca se imprima "-0,00 €".
  return n === 0 ? 0 : n;
}

interface MoneyFormatters {
  currency: string;
  cents: Intl.NumberFormat;
  round: Intl.NumberFormat;
  precise: Intl.NumberFormat;
  /** Decimales de la moneda (2 en euros y pesos mexicanos, 0 en CLP/COP). */
  decimals: number;
}

// Agrupamos siempre los miles, tambien en cifras de cuatro digitos: en una
// columna de importes alineada a la derecha la consistencia se lee mejor.
function buildFormatters(code: string): MoneyFormatters {
  const info = currencyInfo(code);
  const make = (min: number, max: number): Intl.NumberFormat => {
    try {
      return new Intl.NumberFormat(info.locale, {
        style: 'currency',
        currency: info.code,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: min,
        maximumFractionDigits: max,
        useGrouping: true,
      });
    } catch {
      // Un codigo ISO corrupto no puede tumbar la app entera.
      return new Intl.NumberFormat(LOCALE, {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: min,
        maximumFractionDigits: max,
        useGrouping: true,
      });
    }
  };
  return {
    currency: info.code,
    cents: make(info.decimals, info.decimals),
    round: make(0, 0),
    // Costos unitarios (€/kg, $/L): como mucho dos decimales (Alex, 12 oct
    // 2026: «0,2800 €/kg» → «0,28 €/kg»). Lo que no llega a 0,01 se dice
    // con palabras, ver `conMinimo`.
    precise: make(info.decimals, info.decimals),
    decimals: info.decimals,
  };
}

let money = buildFormatters('EUR');

/**
 * Fija la moneda activa de la app. La llama el Layout al cargar el tenant
 * (y al guardarlo en Ajustes). Devuelve true si la moneda ha cambiado,
 * para que quien llama pueda forzar un re-render.
 */
export function configureMoney(currency: string | undefined): boolean {
  const next = currencyInfo(currency).code;
  if (next === money.currency) return false;
  money = buildFormatters(next);
  return true;
}

/** Moneda activa (codigo ISO), por si una pantalla la necesita. */
export function activeCurrency(): string {
  return money.currency;
}

/** Simbolo de la moneda activa (€, $, S/), para sufijos de formularios. */
export function currencySymbol(): string {
  const part = money.cents
    .formatToParts(0)
    .find((p) => p.type === 'currency');
  return part?.value ?? money.currency;
}

/**
 * Un importe que no es cero pero que con los decimales de la moneda saldria
 * «0,00 €» se dice «menos de 0,01 €» (la sal de una receta no cuesta cero:
 * Alex, 12 oct 2026). Uno negativo igual de pequenyo es, a efectos de
 * pantalla, cero.
 */
function conMinimo(formato: Intl.NumberFormat, decimals: number, n: number, sufijo = ''): string {
  const minimo = 10 ** -decimals;
  if (n !== 0 && Math.abs(n) < minimo / 2) {
    return n > 0 ? `menos de ${formato.format(minimo)}${sufijo}` : `${formato.format(0)}${sufijo}`;
  }
  return `${formato.format(n)}${sufijo}`;
}

/** Importe con decimales de la moneda: 1.234,56 € — $12.500 en CLP/COP. */
export function formatMoney(value: unknown): string {
  return conMinimo(money.cents, money.decimals, safe(value));
}

/** Importe redondeado a unidades enteras, para titulares grandes. */
export function formatMoneyShort(value: unknown): string {
  return money.round.format(safe(value));
}

/** Costo unitario: como mucho dos decimales y «menos de 0,01 €» si no llega. */
export function formatUnitCost(value: unknown): string {
  return conMinimo(money.precise, money.decimals, safe(value));
}

/**
 * Costo por unidad con la unidad detras: «18,70 €/kg», «0,28 €/kg»,
 * «$0.28/kg», «25,03 €/L». Dos decimales como mucho (Alex, 12 oct 2026); si
 * no llega al centimo, «menos de 0,01 €/g».
 */
export function formatCostPerUnit(value: unknown, unit: string): string {
  return conMinimo(money.cents, money.decimals, safe(value), `/${unidadVisible(unit)}`);
}

/**
 * Costo por la unidad base del motor (g, ml, pz) dicho como se compra: por kilo
 * o por litro. «4,05 €/kg», nunca «0,00 €/g».
 */
export function formatCostPerBaseUnit(value: unknown, baseUnit: string): string {
  const n = safe(value);
  if (baseUnit === 'g') return formatCostPerUnit(n * 1000, 'kg');
  if (baseUnit === 'ml') return formatCostPerUnit(n * 1000, 'l');
  return formatCostPerUnit(n, baseUnit);
}

/**
 * Porcentaje ya expresado en base 100 (28.4 -> "28,4 %"). Si no es cero pero
 * con esos decimales saldria «0,0 %», se dice «menos de 0,1 %».
 */
export function formatPct(value: unknown, decimals = 1): string {
  const formato = new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return conMinimo(formato, decimals, safe(value), ' %');
}

/** Porcentaje expresado en tanto por uno (0.10 -> "10 %"). */
export function formatRate(value: unknown, decimals = 0): string {
  return formatPct(safe(value) * 100, decimals);
}

/**
 * Porcentaje TAL Y COMO esta configurado, sin redondear ni rellenar ceros:
 * 8,25 sale "8,25 %" y 10 sale "10 %".
 *
 * `formatPct` fija los decimales, asi que un IVA general del 8,25 % salia como
 * "8 %" en las ayudas y parecia que Alexia estaba usando otro tipo del que
 * tienes puesto (lo vio Alex el 1 de septiembre). Para un valor que el usuario
 * ha escrito el, hay que devolverselo igual.
 */
export function formatPctExacto(value: unknown, maxDecimals = 2): string {
  return `${new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  }).format(safe(value))} %`;
}

/** Numero con separador de miles. */
export function formatNumber(value: unknown, decimals = 0): string {
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true,
  }).format(safe(value));
}

/**
 * Cantidad: hasta 3 decimales, sin ceros de relleno y con los separadores del
 * pais («0,5 kg · 1.000 g» en Espanya, «0.5 kg · 1,000 g» en Mexico). La
 * unidad se escribe como se lee: el litro, «L».
 */
export function formatQty(value: unknown, unit?: string, maxDecimals = 3): string {
  const n = safe(value);
  // Gramos y mililitros, sin decimales a partir de 10: «238 g», no «237,6 g».
  const enteros = unit != null && ['g', 'ml', 'mL'].includes(unit) && Math.abs(n) >= 10;
  const text = formatCifra(n, LOCALE, enteros ? 0 : maxDecimals);
  return unit ? `${text} ${unidadVisible(unit)}` : text;
}

/**
 * Formato de compra de un ingrediente con las cifras del pais: «Bolsa de
 * 0,5 kg» en Espanya, «Bolsa de 0.5 kg» en Mexico.
 */
export function formatPurchaseFormat(
  ing: Pick<Ingredient, 'presentation' | 'purchaseQty' | 'purchaseUnit'>,
): string {
  return purchaseFormatLabel(ing, LOCALE);
}

/** Importe con signo explicito, para variaciones y diferencias de precio. */
export function formatSignedMoney(value: unknown): string {
  const n = safe(value);
  const text = formatMoney(Math.abs(n));
  if (n > 0.005) return `+${text}`;
  if (n < -0.005) return `−${text}`;
  return text;
}

const MONTH_NAMES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

/** "2026-03" -> "marzo de 2026" */
export function formatMonthLong(month: string): string {
  const [year, m] = String(month ?? '').split('-');
  const index = Number(m) - 1;
  if (!year || !MONTH_NAMES[index]) return String(month ?? '');
  return `${MONTH_NAMES[index]} de ${year}`;
}

/** "2026-03" -> "mar 26" (para ejes de graficas) */
export function formatMonthShort(month: string): string {
  const [year, m] = String(month ?? '').split('-');
  const index = Number(m) - 1;
  if (!year || !MONTH_NAMES[index]) return String(month ?? '');
  return `${MONTH_NAMES[index].slice(0, 3)} ${year.slice(2)}`;
}

/**
 * "2026-03-14" -> "14 mar" (con el año, "14 mar 2025", si no es el de hoy).
 * Revisión de Alex del 14 oct 2026: fechas siempre «14 sep», nunca
 * «14/09/2026» ni «2026-09-28».
 */
export function formatDate(date: string): string {
  return fechaLegible(String(date ?? ''), hoyLocal());
}

/**
 * Antes «25 de agosto de 2026»; desde el 14 oct 2026, igual que el resto:
 * «25 ago». Se queda el nombre para no tocar a quien la llama.
 */
export function formatDateLong(date: string): string {
  return formatDate(date);
}

/** "2026-03-01" y "2026-08-31" -> "1 mar – 31 ago" (con el año si no es el de hoy) */
export function formatPeriod(start: string, end: string): string {
  return `${formatDate(start)} – ${formatDate(end)}`;
}

/**
 * Lee un numero escrito por el usuario aceptando coma o punto decimal y
 * separadores de miles. Nunca devuelve NaN: si no se entiende, devuelve el
 * valor por defecto.
 *
 * - Con los dos separadores («1.745,50», «$1,200.00»), el ultimo es el decimal.
 * - Solo comas: «1,250,000» siempre son miles. Con una sola coma y tres
 *   cifras detras («1,200»), son miles si el locale usa punto decimal
 *   (Mexico, Estados Unidos) y decimal si usa coma (Espanya: «12,5»).
 *   «0,250» siempre es decimal.
 * - Solo puntos: «1.250.000» son miles; un solo punto es decimal.
 *
 * `decimal` es el separador decimal del locale; por defecto, el del tenant.
 */
export function parseDecimal(value: string, fallback = 0, decimal: string = decimalSeparator()): number {
  let s = String(value ?? '').trim().replace(/[\s\u00a0\u202f$€£]/g, '');
  if (s === '') return fallback;
  const comas = (s.match(/,/g) ?? []).length;
  const puntos = (s.match(/\./g) ?? []).length;
  const miles = (sep: string): boolean =>
    new RegExp(`^[-+]?[1-9]\\d{0,2}(\\${sep}\\d{3})+$`).test(s);
  if (comas > 0 && puntos > 0) {
    const ultimoEsComa = s.lastIndexOf(',') > s.lastIndexOf('.');
    s = ultimoEsComa ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  } else if (comas > 0) {
    if (comas > 1 || (decimal === '.' && miles(','))) {
      if (!miles(',')) return fallback;
      s = s.replace(/,/g, '');
    } else {
      s = s.replace(',', '.');
    }
  } else if (puntos > 1) {
    if (!miles('.')) return fallback;
    s = s.replace(/\./g, '');
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Fecha de hoy en formato YYYY-MM-DD segun el reloj del navegador (no UTC):
 * a las 20:00 en Mexico, «hoy» sigue siendo hoy y no manyana.
 */
export function hoyLocal(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Mes en curso «YYYY-MM» segun el reloj del navegador (no UTC). */
export function mesLocal(date: Date = new Date()): string {
  return hoyLocal(date).slice(0, 7);
}

/** Fecha de hoy en formato YYYY-MM-DD (hora local del navegador). */
export function today(): string {
  return hoyLocal();
}

/** Primera letra en mayuscula, para etiquetas que vienen en minusculas. */
export function capitalize(text: string): string {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}
